import React, { useContext, useEffect, lazy } from 'react';
import { Route, Switch, withRouter, useLocation } from 'react-router-dom';
import { SuspenseWrapper } from './SuspenseWrapper';
import PrivateRoute from './PrivateRoute';

import { helpKey, supportKey } from 'constants/moviDeskKeys';

import { Index } from 'pages/Index';
import { RefreshSession } from 'pages/RefreshSession';
const Login = lazy(() => import('pages/Login'));
import { Logout } from 'pages/Login/Logout';
import { ForgotPassword } from 'pages/Login/ForgotPassword';
import { ResetPassword } from 'pages/Login/ResetPassword';
import { ErrorPage } from 'pages/ErrorPage';
const Audit = lazy(() => import('pages/Reports/Audit'));
const Occurrence = lazy(() => import('pages/Reports/Occurrence'));
const Pmoc = lazy(() => import('pages/Reports/Pmoc'));
const Availability = lazy(() => import('pages/Reports/Availability'));
const ViewGeneralPlansPage = lazy(() => import('pages/Activities/GeneralPlans/ViewGeneralPlansPage'));
const NewGeneralPlanPage = lazy(() => import('pages/Activities/GeneralPlans/NewGeneralPlanPage'));
const EditGeneralPlanPage = lazy(() => import('pages/Activities/GeneralPlans/EditGeneralPlanPage'));
import { AuthContext } from 'context/AuthContext';
import { PrivateGeneralPlansRoute, PrivateScheduleAdminRoute } from 'pages/Activities/GeneralPlans/utils';
const Geolocalize = lazy(() => import('pages/Geolocalize'));
const ReportsPage = lazy(() => import('pages/Reports/ReportsPage'));
const ClientScheduleMonitoring = lazy(() => import('pages/Agendamento/AgendamentoClientes'));
const AdminScheduleMonitoring = lazy(() => import('pages/Admin/AgendamentoAdmin'));
const Activities = lazy(() => import('pages/Reports/Activities'));
const QRcodes = lazy(() => import('pages/Reports/QRcodes'));
const QRcodesList = lazy(() => import('pages/Reports/QRcodesList'));
const Home = lazy(() => import('pages/Home'));
const OccurrenceGeneratedPDF = lazy(() => import('pages/Reports/Occurrence/PDFOcurrence'));
import jwt from 'jwt-decode';
import { PermissionContextProvider } from 'context/PermissionsContext';
import { ConectionError } from 'pages/ErrorPage/ConnectionError';
import { PermissionError } from 'pages/ErrorPage/PermissionError';
import { LandingQR } from 'pages/LandingQR';
const PushNotification = lazy(() => import('pages/Configurations/PushNotification'));
const CompaniesRegistrationPage = lazy(() => import('pages/Registration/Companies'));
const CalendarReport = lazy(() => import('pages/Reports/Calendar'));
import { CompaniesPage } from 'pages/Companies';
import LoginSSO from 'pages/Login/LoginSSO';
const CustomerPortal = lazy(() => import('pages/CustomerPortal'));
import { ChamadoWoba } from 'pages/ChamadoWoba';

const Documents = lazy(() => import('pages/Documents'));
const NewDocument = lazy(() => import('pages/Documents/components/Novo Documento'));
const Edit = lazy(() => import('pages/Documents/components/Edição'));
const TechnicalHours = lazy(() => import('pages/Reports/TechinicalHours'));
const UserAccess = lazy(() => import('pages/Configurations/UserAccess'));
//const NewDashBoard = lazy(() => import('pages/NewDashboard'));
const AssetsReports = lazy(() => import('pages/Reports/Assets'));
const ChamadoConfig = lazy(() => import('pages/Configurations/Chamado'));
const Dashboard = lazy(() => import('pages/NewDashboard/index.jsx'));
const Routes = () => {
  //const { setSocketIoContext } = useContext(SocketIoContext);
  const { authContext } = useContext(AuthContext);
  const { user, token } = authContext;
  const isCommonUser = user?.permissionType === `user`;
  const addMovideskScript = () => {
    const { ussupp } = jwt(token);
    const mdClient = document.createElement('script');
    const movideskScript = document.createElement('script');
    if (user.name !== null) {
      const alreadyExistMdClient = document.getElementById('mdClient');
      if (alreadyExistMdClient) {
        document.body.removeChild(alreadyExistMdClient);
      }
      const alreadyExistMovideskScript = document.getElementById('movideskScript');
      if (alreadyExistMovideskScript) {
        document.body.removeChild(alreadyExistMovideskScript);
      }
      let alreadyExistMdWidget = document.getElementById('md-app-widget');
      if (alreadyExistMdWidget) {
        document.body.removeChild(alreadyExistMdWidget);
      }
      alreadyExistMdWidget = document.getElementById('md-app-widget');
      if (alreadyExistMdWidget) {
        document.body.removeChild(alreadyExistMdWidget);
      }
      const mdKey = ussupp === 'True' ? supportKey : helpKey;
      mdClient.id = 'mdClient';
      mdClient.type = 'text/javascript';
      mdClient.innerText = `var mdChatClient = '${mdKey}';`;
      mdClient.async = false;
      document.body.appendChild(mdClient);
      movideskScript.src = 'https://chat.movidesk.com/Scripts/chat-widget.min.js';
      movideskScript.async = true;
      movideskScript.id = 'movideskScript';
      document.body.appendChild(movideskScript);
    }
  };
  const location = useLocation();
  useEffect(() => {
    if (token) {
      const excludedPaths = [
        '/login',
        '/logout',
        '/home',
        '/configuracoes/acesso-de-usuario',
        '/relatorios/qr-codes-impressao',
      ];
      if (!excludedPaths.includes(location.pathname) && !location.pathname.startsWith('/chamado-woba')) {
        addMovideskScript();
      }
    }
    // eslint-disable-next-line
  }, [token, user]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') {
      if (
        location.pathname !== '/login' &&
        location.pathname !== '/logout' &&
        !location.pathname.startsWith('/chamado-woba')
      ) {
        const script = document.createElement('script');
        script.src = '//tag.goadopt.io/injector.js?website_code=762d4206-3c1c-4c53-bb3a-eebe62d4fbea';
        script.className = 'adopt-injector';
        document.body.appendChild(script);

        const meta = document.createElement('meta');
        meta.name = 'adopt-website-id';
        meta.content = '762d4206-3c1c-4c53-bb3a-eebe62d4fbea';
        document.head.appendChild(meta);

        const script2 = document.createElement('script');
        script2.textContent = 'window.adoptHideAfterConsent=true;';
        document.head.appendChild(script2);

        return () => {
          document.body.removeChild(script);
          document.head.removeChild(meta);
          document.head.removeChild(script2);
        };
      }
    }
  }, [location.pathname]);
  return (
    <PermissionContextProvider>
      <SuspenseWrapper>
        <Switch>
          {/* Erros */}
          {/* <Route path="/error/:code" component={ErrorPage} />

      {/* Refresh session */}
          <Route exact path="/refresh" component={RefreshSession} />
          {/* login */}
          <Route exact path="/login" component={Login} />
          <Route exact path="/login-sso/callback" component={LoginSSO} />
          {/* misc */}
          <Route exact path="/" component={Index} />
          {/* reports */}
          {/* <PrivateRoute
          exact
          path="/relatorios"
          Component={Reports}
        /> */}
          <PrivateRoute exact path="/portal-do-cliente" Component={CustomerPortal} />
          <PrivateRoute exact={true} path="/home" page={'home'} Component={Home} />
          <PrivateRoute exact path="/geolocalizacao" page={'geolocalizacao'} Component={Geolocalize} />
          <PrivateRoute path="/dashboard" page={'dashboard'} Component={Dashboard} />
          <PrivateRoute exact path="/documentos" page={'documentos'} Component={Documents} />
          <PrivateRoute exact path="/documentos/novoDocumento" page={'documentos'} Component={NewDocument} />
          <PrivateRoute exact path="/documentos/:id/editar" page={'documentos'} Component={Edit} />
          <PrivateRoute exact path="/relatorios" page={'relatorios'} Component={ReportsPage} />
          <PrivateRoute exact path="/relatorios/auditoria" page={'relatorios'} Component={Audit} />
          <PrivateRoute
            exact
            path="/relatorios/ocorrencia"
            page={'relatorios'}
            Component={Occurrence}
            // onLeave={() =>
            //   setSocketIoContext({
            //     id: null,
            //     isSocketPending: false,
            //     isSocketActive: false,
            //   })
            // }
          />
          <PrivateRoute
            exact
            path="/relatórios/relatorios-gerados-ocorrencia-pdf"
            page={'relatorios'}
            Component={OccurrenceGeneratedPDF}
          />
          <PrivateRoute exact path="/configuracoes/chamado" page={'config'} Component={ChamadoConfig} />
          <PrivateRoute exact path="/configuracoes/notificacoes-push" page={'config'} Component={PushNotification} />
          <PrivateRoute exact path="/configuracoes/acesso-de-usuario" page={'cadastros'} Component={UserAccess} />
          <PrivateRoute exact path="/relatorios/horas-tecnicas" page={'relatorios'} Component={TechnicalHours} />
          <PrivateRoute exact path="/relatorios/disponibilidade" page={'relatorios'} Component={Availability} />
          <PrivateRoute exact path="/relatorios/pmoc" page={'relatorios'} Component={Pmoc} />
          <PrivateRoute exact path="/relatorios/atividades" page={'relatorios'} Component={Activities} />
          <PrivateRoute exact path="/relatorios/qr-codes" page={'relatorios'} Component={QRcodes} />
          <PrivateRoute path="/relatorios/qr-codes-impressao" page={'relatorios'} Component={QRcodesList} />
          <PrivateRoute path="/cadastros/empresas" page={'cadastros'} Component={CompaniesRegistrationPage} />
          <PrivateRoute path="/relatorios/calendario-atividades" page={'relatorios'} Component={CalendarReport} />
          <PrivateRoute path="/relatorios/ativos" page={'relatorios'} Component={AssetsReports} />
          <PrivateGeneralPlansRoute
            isDisabled={isCommonUser}
            exact
            path="/atividades/planos-gerais"
            page={'planosGerais'}
            Component={ViewGeneralPlansPage}
          />
          <PrivateGeneralPlansRoute
            isDisabled={isCommonUser}
            exact
            page={'planosGerais'}
            path="/atividades/planos-gerais/novo"
            Component={NewGeneralPlanPage}
          />
          <PrivateGeneralPlansRoute
            isDisabled={isCommonUser}
            exact
            page={'planosGerais'}
            path="/atividades/planos-gerais/:id"
            Component={EditGeneralPlanPage}
          />
          <Route path="/monitoria-de-agendamento-cliente" component={ClientScheduleMonitoring} />
          <PrivateScheduleAdminRoute
            isDisabled={isCommonUser}
            exact
            path="/monitoria-de-agendamento-admin"
            component={AdminScheduleMonitoring}
          />
          <Route path="/conta/recuperar" component={ForgotPassword} />
          <Route path="/logout" component={Logout} />
          <Route exact path="/conta/redefinir-senha/:token" component={ResetPassword} />
          <Route exact path="/conection-error" component={ConectionError} />
          <Route exact path="/permission-error" component={PermissionError} />
          <Route exact path="/qr/:code" component={LandingQR} />
          <Route exact path="/clientes/:subdominio" component={CompaniesPage} />
          <Route exact path={'/chamado-woba/:qrcode'} component={ChamadoWoba} />
          {/* 404 */}
          <Route path="*">
            <ErrorPage errorCode="404" />
          </Route>
        </Switch>
      </SuspenseWrapper>
    </PermissionContextProvider>
  );
};

export default withRouter(Routes);
